<template>
  <LoginContainer
    class="page"
    :background="true"
  >
    <div
      v-if="error || authError"
      class="px-md absolute left-0 right-0 top-sm"
    >
      <OrNotification
        class="sm:max-w-[768px] mx-auto"
        variant="error"
        disable-close
      >
        {{ $t('auth.external_provider.error', { errorMessage: error ? error : authError }) }}
        <OrButton
          variant="link"
          @click="logout"
        >
          {{ $t('multi_user.set_password.Sign in') }}
        </OrButton>
      </OrNotification>
    </div>
    <div
      v-else
      class="layout-column items-center"
    >
      <OrLoader class="mb-sm" />
      <div class="font-display-small">
        {{ $t('auth.external_provider.loggingIn') }}
      </div>
    </div>
  </LoginContainer>
</template>

<script lang="ts">

import {
  OrButtonV3 as OrButton,
  OrLoaderV3 as OrLoader,
  OrNotificationV3 as OrNotification,
} from '@onereach/ui-components';
import _ from 'lodash';
import { storeToRefs } from 'pinia';
import { defineComponent, ref } from 'vue';

import { useAuthStore } from '@/stores/auth/auth';
import { authUiUrl } from '@/utils/authUiRuntime';

import authService from '../api/auth';
import { authenticateExternalProvider } from '../api/auth_provider';
import { useAuth } from '../composables/useAuth';
import config from '../config';
import TranslatableError from '../errors/TranslatableError';
import helpers from '../helpers/index';
import LoginContainer from '../layouts/LoginContainer.vue';

export default defineComponent({
  components: {
    OrNotification,
    LoginContainer,
    OrLoader,
    OrButton,
  },
  setup() {
    const { getProviderByName } = useAuth();

    const authStore = useAuthStore();

    const {
      authorized,
      reactiveToken: token,
      error: authError,
    } = storeToRefs(authStore);

    const error = ref('');
    const authenticating = ref(false);
    const redirectPath = ref('');

    return {
      getProviderByName,
      authStore,
      authorized,
      token,
      authError,
      error,
      authenticating,
      redirectPath,
    };
  },
  watch: {
    authorized: {
      immediate: true,
      handler(value) {
        if (value) {
          // this.authorizeRedirect();
        }
      },
    },
    authError(value) {
      if (value) {
        this.authenticating = false;
        this.error = 'The email / password combination is not valid.';
      }
    },
  },

  async created() {
    const { code, token, entityId, tokenType } = this.$route.query;
    const destinationUrl = (this.$route.query.destination_url as string) || null;
    this.authStore.setDestinationUrl(destinationUrl);
    let state;
    try {
      state = JSON.parse(this.$route.query.state as string);
    } catch (error) {
      if (!token) {
        throw new TranslatableError('errors.login.Invalid or malformed OAuth state parameter', error);
      }
    }
    const providerName = this.$route.params.providerName as string;
    const provider = this.getProviderByName(providerName);
    if (_.isEmpty(provider)) {
      this.error = 'Unsupported provider';
      return;
    }
    try {
      if (token) {
        // In case if provider retruned Single User Token we use this one
        if (tokenType === 'multi-user') {
          const user = await authService.validateMultiUserToken(token.toString());
          return this.authStore.signInWithMultToken({
            user,
            keepLoggedIn: false,
          });
        }
        await this.signIn({
          provider,
          entityId,
          state,
          token,
        });
      } else {
        if (_.isEmpty(code)) {
          this.error = 'No Authentication code';
          return;
        }
        // in case of code (get retruned from provider and used to authenticate on our side via okta/provider backend)
        let redirectUri = `${window.location.origin}${window.location.pathname}`;
        let { userId, accountId } = state;
        // authenticating/decoding "code" to OR user
        const userAccount = await authenticateExternalProvider(
          provider?.providerPath!,
          code as string,
          redirectUri,
          {
            userId,
            accountId,
          },
        );

        if (userAccount.tokenType === 'multi-user') {
          return this.authStore.signInWithMultToken({
            user: userAccount,
            keepLoggedIn: sessionStorage.getItem('keepLoggedIn') === 'true',
          });
        }

        await this.signIn({
          provider,
          entityId: userAccount.entityId,
          state,
          token: userAccount.token,
        });
      }
      // Check for noClose query parameter to prevent window closing
      const noClose = this.$route.query.noClose === 'true';
      if (window.opener && !noClose) window.close(); // close window if it was login overlay
    } catch (error) {
      this.error = _.get(error, 'body.error', '');
      if (!this.error) {
        throw new TranslatableError('errors.commonText', error);
      }
    }
  },

  methods: {
    async signIn({ provider, entityId, state, token }) {
      this.assignRedirectPath(provider, entityId, state);
      await this.authStore.signInWithToken({
        token,
        allowGuestLogin: true,
      });
      if (!this.error && !this.authError) {
        this.authorizeRedirect(state);
      }
    },

    authorizeRedirect(state) {
      if (_.isEmpty(state)) state = this.$route.query;
      if (state) {
        const isChild = _.get(state, 'isChild', false);
        if (isChild) {
          window.close();
          return;
        }
      }


      const useDestinationUrl = !!this.$route.query.destination_url;
      let targetPath = (this.$route.query.destination_url as string) || this.redirectPath;
      if (config.ADD_AUTH_TOKEN && !useDestinationUrl) {
        targetPath = helpers.addQueryParams(targetPath, `token=${encodeURIComponent(`${this.token}`)}`);
      }

      window.location.href = targetPath;
    },

    assignRedirectPath(provider, entityId, state) {
      const redirectPath = provider.redirectPath || authUiUrl;
      let uri = `${redirectPath}${entityId ? entityId : ''}`;
      if (state) {
        const queryParams = _.map(state, (value, key) => `${key}=${encodeURIComponent(value)}`).join('&');
        uri = helpers.addQueryParams(uri, queryParams);
      }
      this.redirectPath = uri;
    },
    logout() {
      this.$router.push('/logout');
    },
  },
});
</script>
